import { Usagemanager } from '@src/endpoints/services/usagemanager.service'
import { toastInfo } from '@src/utils/toast'
import { getAccountNameById } from '@src/utils/accounts'
import {
  defaultState,
  UsageItem,
  UsageState
} from '../reducers/user.usage/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import type { State } from '../configureStore'
import { UserUsage as ActionTypes } from '../actionTypes'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import type { FilterAccount } from '../reducers/filter'
import type { TableOptions } from '../reducers/user.usage'
import { isEmpty } from 'lodash'

// Actions
const getStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_STAT_SUCCESS,
  defaultState
)
type getStatSuccessAction = ReturnType<typeof getStatSuccess>

export const setTableOptions = (data: TableOptions) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_TABLE_OPTIONS, data)
type setTableOptionsAction = ReturnType<typeof setTableOptions>

export const setTimestamp = (data: { startTime: Date; endTime: Date }) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_TIMESTAMP, data)
type setTimestampAction = ReturnType<typeof setTimestamp>

const changeColumnFilter = (column: string) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.ON_CHANGE_COLUMN_FILTER,
    { column }
  )
type changeColumnFilterAction = ReturnType<typeof changeColumnFilter>

const changeSerachFilter = (searchValue: string) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.ON_CHANGE_SEARCH_FILTER,
    { searchValue }
  )
type changeSerachFilterAction = ReturnType<typeof changeSerachFilter>

const getUserUsageSuccess = (
  data: {
    tableOptions: TableOptions
    pagesCount: number
    usageInfoList: UsageItem[]
  },
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_USER_USAGE_SUCCESS, {
    ...data,
    isPending
  })
type getUserUsageSuccessAction = ReturnType<typeof getUserUsageSuccess>

const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export const cleanerStat = ReduxHelpers.Common.Actions.cleanerState(
  ActionTypes.CLEANER_STAT,
  defaultState
)

type cleanerStatAction = ReturnType<typeof cleanerStat>

export const cleaning = (data: { startTime: Date; endTime: Date }) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.CLEANING, data)

type cleaningAction = ReturnType<typeof cleaning>

// Action Creators

export function getCTLogDetails(event_id: any) {
  return (dispatch: (args: any) => void, getStore: () => State) => {
    dispatch(requestPending(true))
    Usagemanager.GetUsageEventRaw({ params: { event_id } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        const { unparsed_event } = res
        if (!isEmpty(unparsed_event)) {
          dispatch(getStatSuccess('logDetails', unparsed_event))
        } else {
          toastInfo(`Detail not available.`)
          dispatch(requestPending(false))
        }
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export function clearCTLogDetails() {
  return (dispatch: any, getStore: () => State) => {
    dispatch(cleanerStat('logDetails'))
  }
}

export interface OnChangeFilterColumnParams {
  query: string
  userName: string
  accountId: string
  column: string
}
export function onChangeFilterColumn(
  data: OnChangeFilterColumnParams,
  showToastMessages: boolean
) {
  return (dispatch: any, getStore: () => State) => {
    const { userUsage } = getStore()
    const { startTime, endTime, tableOptions } = (userUsage || {}) as UsageState
    dispatch(changeColumnFilter(data.column))
    if (startTime && endTime) {
      dispatch(
        getUserUsageData(
          {
            query: data.query,
            userName: data.userName,
            accountId: data.accountId,
            startTime,
            endTime,
            tableOptions: {
              ...tableOptions,
              searchFields: data.column ? [data.column] : [],
              searchValue: !data.column ? '' : tableOptions.searchValue
            }
          },
          showToastMessages
        )
      )
    }
  }
}

export interface OnChangeSearchParams {
  query: string
  userName: string
  accountId: string
  searchValue: string
}

export function onChangeSearch(data: OnChangeSearchParams) {
  return (dispatch: any, getStore: () => State) => {
    const { userUsage } = getStore()
    const { startTime, endTime, tableOptions } = userUsage || ({} as UsageState)
    dispatch(changeSerachFilter(data.searchValue))
    if (startTime && endTime) {
      dispatch(
        getUserUsageData({
          query: data.query,
          userName: data.userName,
          accountId: data.accountId,
          startTime,
          endTime,
          tableOptions: {
            ...tableOptions,
            searchValue: data.searchValue
          }
        })
      )
    }
  }
}

export interface FetchByDateRangeParams {
  query: string
  userName: string
  accountId: string
  startTime: Date
  endTime: Date
}

export function fetchByDateRange(
  data: FetchByDateRangeParams,
  showToastMessages: boolean
) {
  return (dispatch: any, getStore: () => State) => {
    const { userUsage } = getStore()
    const { tableOptions = {} } = (userUsage || {}) as UsageState
    dispatch(getStatSuccess('startTime', data.startTime))
    dispatch(getStatSuccess('endTime', data.endTime))
    if (data.startTime && data.endTime) {
      dispatch(
        getUserUsageData(
          {
            query: data.query,
            userName: data.userName,
            accountId: data.accountId,
            startTime: data.startTime,
            endTime: data.endTime,
            tableOptions
          },
          showToastMessages
        )
      )
    }
  }
}

export interface FetchDataParams {
  query: string
  userName: string
  accountId: string
}

export function fetchData(data: FetchDataParams, showToastMessages: boolean) {
  return (dispatch: any, getStore: () => State) => {
    const { userUsage } = getStore()
    const { startTime, endTime } = (userUsage || {}) as UsageState
    dispatch(cleaning({ startTime, endTime }))
    if (startTime && endTime) {
      dispatch(
        getUserUsageData(
          {
            query: data.query,
            userName: data.userName,
            accountId: data.accountId,
            ...defaultState,
            startTime,
            endTime
          },
          showToastMessages
        )
      )
    }
  }
}

export interface FetchDataByGridParams {
  query: string
  userName: string
  accountId: string
  pageNumber: number
  pageSize: number
  sortField: string
  asc: boolean
}

export function fetchDataByGrid(
  data: FetchDataByGridParams,
  showToastMessages: boolean
) {
  return (dispatch: any, getStore: () => State) => {
    const { userUsage } = getStore()
    const {
      startTime,
      endTime,
      tableOptions = {}
    } = (userUsage || {}) as UsageState
    const { searchValue, searchFields } = (tableOptions || {}) as any
    dispatch(
      setTableOptions({
        ...tableOptions,
        pageNumber: data.pageNumber,
        pageSize: data.pageSize,
        sortField: data.sortField,
        asc: data.asc
      })
    )
    if (startTime && endTime) {
      dispatch(
        getUserUsageData(
          {
            query: data.query,
            userName: data.userName,
            accountId: data.accountId,
            startTime,
            endTime,
            tableOptions: {
              pageNumber: data.pageNumber,
              pageSize: data.pageSize,
              sortField: data.sortField,
              asc: data.asc,
              searchValue,
              searchFields
            }
          },
          showToastMessages
        )
      )
    }
  }
}

export interface GetUserUsageDataParams {
  query: string | null
  userName: string
  accountId: string
  startTime: Date
  endTime: Date
  tableOptions: TableOptions
}

export function getUserUsageData(
  data: GetUserUsageDataParams,
  showToastMessages?: boolean,
  callback?: () => any
) {
  return (dispatch: any, getStore: () => State) => {
    dispatch(requestPending(true))
    const store = getStore()
    const { filter } = store
    const { currentAccount = {} as FilterAccount } = filter
    const params = {
      username: data?.userName || '',
      account_id: data?.accountId || currentAccount?.id || '',
      'table_options.page_number':
        data?.tableOptions?.pageNumber || defaultState.tableOptions.pageNumber,
      'table_options.page_size':
        data?.tableOptions?.pageSize || defaultState.tableOptions.pageSize,
      'table_options.sorted_field':
        data?.tableOptions?.sortField || defaultState.tableOptions.sortField,
      'table_options.sorted_asc': !!data?.tableOptions?.asc,
      'table_options.search_filter':
        data?.tableOptions?.searchValue ||
        defaultState.tableOptions.searchValue,
      'table_options.search_fields':
        data?.tableOptions?.searchFields ||
        defaultState.tableOptions.searchFields,
      'table_options.query': data?.query || defaultState.query,
      start_time: (data?.startTime || defaultState?.startTime)?.toISOString(),
      end_time: (data?.endTime || defaultState?.endTime)?.toISOString()
    }
    Usagemanager.GetUsageData({ params })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        const { auth } = getStore()
        const { tenant } = auth
        const { account_info = [] } = tenant || {}
        const { pages_count = 0, usage_info_list = [] } = res || {}
        dispatch(
          getUserUsageSuccess({
            tableOptions: data.tableOptions,
            pagesCount: pages_count,
            usageInfoList: (usage_info_list || []).map(x => ({
              ...x,
              identity_account: x.identity_account_id
                ? getAccountNameById(x.identity_account_id, account_info)
                : null,
              resource_account: x.resource_account_id
                ? getAccountNameById(x.resource_account_id, account_info)
                : null
            }))
          })
        )
        if (showToastMessages) {
          if (usage_info_list && usage_info_list.length === 0) {
            toastInfo('No matching records')
          }
        }
        if (callback) callback()
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export type ActionTypes =
  | getStatSuccessAction
  | setTimestampAction
  | changeColumnFilterAction
  | changeSerachFilterAction
  | getUserUsageSuccessAction
  | requestPendingAction
  | cleanerStatAction
  | cleaningAction
  | setTableOptionsAction
